import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import megatron from 'megatron';

import * as S from './reports.styles';

import Layout from './../../stories/components/common/Layout';
import TransactionReport from './../../stories/components/Reports/TransactionReport';
import CommissionReport from './../../stories/components/Reports/CommissionReport';
import Shimmer from './../../stories/components/common/Shimmer/Shimmer';
import {logPageLoadEvents, logOmniClick} from '@ibibo/analytics';
import {userSelector} from '@ibibo/user-reducer';
import {getCookie} from './../../utils/getCookie';
import {redirectToSameWindowWithRefresh, getCrid} from './../../utils/redirectWithCrId';
import {useAnalyticService} from '@ibibo/analytics-provider';

const Reports = function() {
  const [reportData, setReportData] = useState(null);
  const [menuSelect, setMenuSelect] = useState(null);
  const [errorMsg, UpdateErr] = useState('');
  const userInfoSelector = useSelector(userSelector);
  const userInfo = userInfoSelector?.userInfo;
  const account_id = userInfo?.account_id ?? '';
  const ccdeCookie = getCookie('ccde');
  const isGCC = ccdeCookie == 'AE';
  const {updateAnalyticDataStore, sendTrackingDataToSdk} = useAnalyticService();
 
  useEffect(() => {
    redirectToSameWindowWithRefresh('/coreui/mybookings');
  }, []);
  const fetchReportsData = async () => {
    const url = `${__ENV_CONFIG__.MYPARTNER}/transactions/report-template`;

    return await megatron({
      url,
      name: 'FETCH REPORT LAYOUT',
      type: 'GET',
      credentials: 'include',
    });
  };
  const loadPDTTrackingData = ({userData, walletInfo}) => {
    const {account_id, is_sub_user, uuid} = userData;

    const pdtTrackingData = {
      global: {
        payload: {
          campaign_details: {
            campaign_name: 'mypartner_direct',
            campaign_key: 'MYPARTNER',
          },
          event_tracking_context: {
            traffic_source: 'mypartner_direct',
            domain_country: 'india',
            domain_currency: 'INR',
            domain_language: 'eng',
            env: 'prod',
            bu: 'mypartner_core',
            organisation: 'MMT',
            funnel_source: 'mypartner_core',
            topic_name: 'mmt_core_mypartner_pdt_client_logging',
            producer: 'mypartner_core',
            template_id: 179775,
          },
          user_context: {
            user_type: is_sub_user ? 'Sub User' : 'Admin',
            agency_code: account_id?.value,
            profile_type: 'cta',
            uuid: uuid?.value || '',
            is_logged_in: true,
            logging_channel: null,
            wallet_bal: walletInfo?.tb,
          },
        },
      },
      pageLoad: {
        payload: {
          page_context: {
            lob: 'common',
            lob_category: 'common',
            page_title: null,
            page_name: 'commissionReport',
            subpage_name: null,
            site_section: null,
            page_type: 'landing',
            percent_page_viewed: null,
            prev_page_name: null,
            travel_store: null,
            page_url: null,
            funnel_step: 'landing',
            page_state_journey: null,
            sub_lob: 'mypartner_core',
            pg_time_interval: null,
            platform: null,
          },
          coupon_details: null,
          cross_sell_details: null,
          addon_details: null,
          traveller_details_list: null,
          payment_details: null,
          campaign_details: null,
          event_detail: {
            event_name: 'page_entry',
            event_type: 'user_action',
            event_value: 'commissionReport',
            components: null,
            client_travel_store_meta: null,
            interaction_type: null,
            booking_info: null,
          },
        },
      },
    };
    if (pdtTrackingData) {
      updateAnalyticDataStore?.(pdtTrackingData, 'COMMISSION_REPORT');
      sendTrackingDataToSdk?.(pdtTrackingData, 'COMMISSION_REPORT');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchReportsData();
        if (response?.error?.redirectUrl) {
          window.location.href = response.error.redirectUrl;
          return;
        }
        setReportData(response);
        setMenuSelect(response.sidebar.defaultSelection);

        logPageLoadEvents('reports:mysettings', {
          eVar15: `myPartner_${response?.sidebar?.defaultSelection?.data}Report|mypartner`,
          eVar67: `${account_id || ''}`, //mypartner agent id
          prop27: '/agent/transactionreport/',
          eVar37: isGCC
            ? 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng'
            : 'domain_sbu:mypartner|domain_country:india|domain_currency:inr|domain_language:eng',
        });
      } catch (e) {
        UpdateErr('Unable to load reporting section. Please try again.');
      }
    })();
  }, []);

  useEffect(() => {
    if (userInfo) {
      loadPDTTrackingData({userData: userInfo, walletInfo: userInfoSelector?.walletInfo});
    }
  }, [userInfo]);

  if (errorMsg) {
    return (
      <S.ErrorContainer>
        {errorMsg && <S.Description dangerouslySetInnerHTML={{__html: errorMsg}} />}
      </S.ErrorContainer>
    );
  }
  if (!reportData || !menuSelect) {
    return (
      <S.Loader>
        <S.LeftLoader>
          {[1, 2, 3, 4, 5].map((data, index) => {
            return (
              <div key={index}>
                <Shimmer
                  width={'90%'}
                  height={'50px'}
                  marginLeft="20px"
                  marginBottom="8px"
                  marginTop="20px"
                />
              </div>
            );
          })}
        </S.LeftLoader>
        <S.RightLoader>
          {[1, 2, 3, 4, 5].map((data, index) => {
            return (
              <div key={index}>
                <Shimmer width={'90%'} height={'50px'} marginBottom="8px" marginTop="20px" />
              </div>
            );
          })}
        </S.RightLoader>
      </S.Loader>
    );
  }

  if (menuSelect?.order === 'gstInvoices') {
    redirectToSameWindowWithRefresh('/agent/gstinvoice');
  }

  const selectedMenuData = (function() {
    if (menuSelect?.order === 'gstInvoices') {
      return null;
    }
    let selectedData;
    reportData?.sidebar?.order?.forEach(orderItem => {
      if (orderItem === menuSelect?.order && reportData?.sidebar?.data[menuSelect?.order]) {
        const orderData = reportData.sidebar.data[orderItem];
        orderData?.order?.forEach(item => {
          if (item === menuSelect?.data && orderData?.data?.[item]) {
            selectedData = orderData.data[item];
          }
        });
      }
    });
    return selectedData;
  })();
  return (
    <S.ReportContainer>
      <Layout
        heading={reportData?.sidebar?.primaryText}
        title={selectedMenuData?.header?.primaryText}
        summary={selectedMenuData?.header?.secondaryText}
        data={reportData?.sidebar?.hierarchyData}
        selectedMenu={menuSelect}
        onMenuSelect={data => {
          if (data?.order?.toLowerCase()?.indexOf('gst') !== -1) {
            logOmniClick('reports:mysettings:gstInvoice', {
              prop54: `gstInvoice_clicked`,
              eVar15: `myPartner_gstInvoice|mypartner`,
              eVar37: isGCC
                ? 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng'
                : 'domain_sbu:mypartner|domain_country:india|domain_currency:inr|domain_language:eng',
            });

            //This will load the existing gst reporting module

            redirectToSameWindowWithRefresh('/agent/gstinvoice');

            return;
          }

          logOmniClick('reports:mysettings:reportSelection', {
            prop54: `${data?.data}_clicked`,
            eVar15: `myPartner_${data?.data}Report|mypartner`,
            eVar37: isGCC
              ? 'domain_sbu:mypartner|domain_country:ae|domain_currency:aed|domain_language:eng'
              : 'domain_sbu:mypartner|domain_country:india|domain_currency:inr|domain_language:eng',
          });
          setMenuSelect(data);
        }}
      >
        {menuSelect.data === 'transaction' && <TransactionReport data={selectedMenuData} />}
        {menuSelect.data === 'commission' && (
          <CommissionReport data={selectedMenuData} sendTrackingDataToSdk={sendTrackingDataToSdk} />
        )}
        {/* {menuSelect === 'tdsCertificate' && <TDSCertificate />} */}
      </Layout>
    </S.ReportContainer>
  );
};
export default Reports;
